import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { useClientRender } from '~/hooks/use-client-render';

import {
  type HeroTakeoverFormalDegreeKey,
  type HeroTakeoverHeadline,
  type HeroTakeoverVariant,
  getHeroTakeoverCheckoutHref,
  HERO_TAKEOVER_CTA_LABEL,
  HERO_TAKEOVER_PAGE_CONTENT,
  HERO_TAKEOVER_STRIKETHROUGH_IMAGE,
} from '../constants/hero-content';

import { HeroTakeoverBadge } from './hero-takeover-badge';

export function HeroTakeoverSection({
  formalDegreeKey,
  variant,
  downloadBrochureButtonSlot = null,
}: {
  formalDegreeKey: HeroTakeoverFormalDegreeKey;
  variant: HeroTakeoverVariant;
  downloadBrochureButtonSlot?: ReactNode;
}) {
  const config = HERO_TAKEOVER_PAGE_CONTENT[formalDegreeKey];
  const headline: HeroTakeoverHeadline = config.headlines[variant];
  const { data: promotion } = usePromotion();

  // Mirrors AccreditationHeroSection: subscribers add the degree application to their subscription,
  // everyone else buys the standalone degree application. CLS-safe: SSR renders the non-subscriber
  // href, then the client corrects it for subscribers (same flash as the default hero).
  const clientReady = useClientRender();
  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;
  const checkoutHref = getHeroTakeoverCheckoutHref({
    formalDegreeKey,
    hasActiveSubscription,
    promoCode: promotion?.promoCode,
  });

  return (
    <HeroLayout
      backgroundImageOverlayGradient={null}
      bgColor={config.backgroundColor}
      bgGradient={config.viewportBackgroundGradient}
      textAndCtaContainerProps={{ gap: { base: '24px', md: '40px' } }}
      backgroundImageSlot={
        <HeroTakeoverBadge
          foregroundRingRepetitions={config.badgeForegroundRingRepetitions}
          phrase={config.badgePhrase}
        />
      }
      ctaSlot={
        <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }}>
          <ButtonLink
            linkProps={{ href: checkoutHref }}
            buttonProps={{
              leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
              size: 'icon-left',
              variant: 'solid',
              colorScheme: 'blue',
              width: { base: '100%', md: 'fit-content' },
              onClick: () => {
                trackEvent({
                  type: 'click',
                  name: 'Masters Page CTA Clicked',
                  label: 'hero_enroll_now',
                  additionalAttributes: {
                    formal_degree_key: formalDegreeKey,
                    hero_takeover_variant: variant,
                  },
                });
              },
            }}
          >
            {HERO_TAKEOVER_CTA_LABEL}
          </ButtonLink>
          {downloadBrochureButtonSlot}
        </Flex>
      }
      textContentSlot={
        <Flex
          flexDirection='column'
          gap={{ base: '16px', md: '24px' }}
          maxWidth={{ base: '100%', md: '500px', lg: '600px', xl: '800px' }}
          pt={{ base: '180px', md: '0' }}
          whiteSpace='pre-wrap'
        >
          <Heading
            as='h1'
            color='white'
            fontSize={{ base: '38px', md: '46px', lg: '56px', xl: '76px' }}
            fontWeight={{ base: '600', md: '500' }}
            letterSpacing={{ base: '-0.76px', md: '-0.92px', lg: '-1.12px', xl: '-1.52px' }}
            lineHeight={{ base: '1.1', md: '1.05', xl: '1' }}
          >
            <Box as='span' color='#EDA93B' display='block'>
              {headline.eyebrow}
            </Box>
            {headline.prefix}
            <Box as='span' display={headline.priceOnOwnLine ? 'block' : 'inline'}>
              {headline.priceLabel}
              {headline.originalPrice ? (
                <Box as='span' color='#9C9FA8' position='relative'>
                  {headline.originalPrice}
                  {/*
                   * Anchors the slash across the original price. The box height only sets where the
                   * slash is centred — the graphic itself is wider than it is tall and is sized off
                   * the price width below, so it deliberately overhangs this box vertically.
                   */}
                  <Box
                    h={{ base: '6px', md: '12px' }}
                    left='0'
                    pointerEvents='none'
                    position='absolute'
                    right='0'
                    top={{ base: '50%', md: '55%' }}
                    transform='translateY(-50%)'
                    aria-hidden
                  >
                    <Image
                      alt=''
                      height={75}
                      src={HERO_TAKEOVER_STRIKETHROUGH_IMAGE}
                      // Scale to the price width rather than the intrinsic 400x75, which would
                      // otherwise run across the sale price and the rest of the headline.
                      style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                      width={400}
                    />
                  </Box>
                </Box>
              ) : null}
              {headline.salePrice}
            </Box>
          </Heading>
          <Text color={{ base: '#9C9FA8', md: '#CECFD4' }} fontSize={{ base: '16px', md: '18px' }} lineHeight='1.5'>
            {config.subtitle}
          </Text>
        </Flex>
      }
    />
  );
}
